import React from "react";

/**
 * The stages of a workflow, with one active and the rest secondary.
 *
 * RESEARCH-EXPLORATION-REDESIGN, 2026-09-10. Built for the Creative Session's
 * eight stages but deliberately generic — it takes a list and an index, so the
 * wizard's own indicator can move onto it later rather than a second rail being
 * written.
 *
 * EIGHT STOPS, NOT SEVEN. Founder ruling: "Use the honest 8-stop rail with the
 * real step names, not a forced 7 to match the mockup." Exploration is a real
 * stage that happens BEFORE `creativeSessionStep.STEPS`, and those seven are
 * Psychology Map, Angle, Hook, Script, CTA, Creative Elements, Prompts. A rail
 * showing seven with Exploration first would have to drop or rename one of them,
 * and a rail that miscounts the workflow is worse than one that looks busier.
 *
 * ACCESSIBILITY. The skill's navigation rules: the active stage is marked with
 * `aria-current="step"` and stated IN WORDS as well as by colour, because colour
 * alone is not a position. Stages the operator cannot navigate to are rendered
 * as plain items rather than dead buttons.
 */
export default function StageRail({ stages, activeIndex, onGoTo,
                                    testid = "stage-rail" }) {
  if (!Array.isArray(stages) || stages.length === 0) return null;
  const active = Math.max(0, Math.min(activeIndex ?? 0, stages.length - 1));

  return (
    <nav className="stage-rail" data-testid={testid}
         aria-label={`Workflow stages — stage ${active + 1} of ${stages.length}`}>
      {/* Said in words, for a screen reader and for anyone who cannot separate
          the active colour from the rest. */}
      <p className="sr-only">
        Stage {active + 1} of {stages.length}: {stages[active].label}
      </p>
      <ol className="stage-rail-list">
        {stages.map((s, i) => {
          const isActive = i === active;
          const isDone = i < active;
          const reachable = typeof onGoTo === "function" && s.reachable !== false;
          const cls = `stage${isActive ? " is-active" : ""}${isDone ? " is-done" : ""}`;
          const body = (
            <>
              <span className="stage-n" aria-hidden="true">{i + 1}</span>
              <span className="stage-text">
                <span className="stage-label">{s.label}</span>
                {s.hint ? <span className="stage-hint">{s.hint}</span> : null}
              </span>
            </>
          );
          return (
            <li key={s.id || i} className={cls}
                aria-current={isActive ? "step" : undefined}
                data-testid={`${testid}-${i}`}>
              {reachable && !isActive ? (
                <button type="button" className="stage-hit"
                        onClick={() => onGoTo(i)}
                        data-testid={`${testid}-go-${i}`}>
                  {body}
                </button>
              ) : (
                <span className="stage-hit">{body}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
